const MAX_REPLY_DEPTH = 5;

function normalizeId(value) {
  return String(value || "").trim();
}

function normalizeReply(reply, fallbackId = "") {
  if (!reply) return null;
  const id = normalizeId(reply.id || fallbackId);
  return id ? { ...reply, id } : null;
}

/**
 * Resolve a notification reply into its root-to-parent path plus the exact
 * target. The loader is injected so traversal remains independently testable.
 */
export async function resolveReplyFocus({ replyId, loadReply }) {
  const targetId = normalizeId(replyId);
  if (!targetId || typeof loadReply !== "function") {
    return { focusPath: [], targetReply: null, targetReplyId: targetId };
  }

  const targetReply = normalizeReply(await loadReply(targetId), targetId);
  if (!targetReply) {
    return { focusPath: [], targetReply: null, targetReplyId: targetId };
  }

  const declaredAncestors = Array.isArray(targetReply.ancestors)
    ? targetReply.ancestors.map(normalizeId).filter(Boolean)
    : [];
  const ancestorIds = Array.from(new Set(declaredAncestors))
    .filter((id) => id !== targetId)
    .slice(-MAX_REPLY_DEPTH);

  if (ancestorIds.length) {
    const ancestors = await Promise.all(
      ancestorIds.map(async (id) => normalizeReply(await loadReply(id), id))
    );
    return {
      focusPath: ancestors.filter(Boolean),
      targetReply,
      targetReplyId: targetId,
    };
  }

  const reversedPath = [];
  const visited = new Set([targetId]);
  let parentId = normalizeId(targetReply.parentId);

  while (parentId && reversedPath.length < MAX_REPLY_DEPTH && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = normalizeReply(await loadReply(parentId), parentId);
    if (!parent) break;
    reversedPath.push(parent);
    parentId = normalizeId(parent.parentId);
  }

  return {
    focusPath: reversedPath.reverse(),
    targetReply,
    targetReplyId: targetId,
  };
}

export function mergeFocusedReplies(rows, focusedReplies, parentId = null) {
  const expectedParentId = normalizeId(parentId) || null;
  const next = Array.isArray(rows) ? [...rows] : [];
  const present = new Set(next.map((reply) => normalizeId(reply?.id)).filter(Boolean));

  (focusedReplies || []).forEach((reply) => {
    const normalized = normalizeReply(reply);
    const replyParentId = normalizeId(normalized?.parentId) || null;
    if (!normalized || replyParentId !== expectedParentId || present.has(normalized.id)) return;
    next.push(normalized);
    present.add(normalized.id);
  });

  return next;
}

export function replyElementId(replyId) {
  const id = normalizeId(replyId);
  return id ? `reply-${id}` : undefined;
}

export function firstChildReplyId(rows, parentId = null) {
  const expectedParentId = normalizeId(parentId) || null;
  const firstChild = (rows || []).find((reply) => {
    const replyParentId = normalizeId(reply?.parentId) || null;
    return replyParentId === expectedParentId && normalizeId(reply?.id);
  });
  return normalizeId(firstChild?.id);
}
